import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Award, Globe, Users as UsersIcon, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { divisions, stats } from '../mockData';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-background to-background -z-10" />
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 md:mb-6">
              About <span className="text-primary">ALM Group</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed px-4">
              Building bridges between vision and reality through excellence in trading, consulting, and technology
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-12 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Images first on mobile */}
            <div className="grid grid-cols-2 gap-3 md:gap-4 order-1 md:order-2">
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab"
                alt="Modern Office"
                className="rounded-lg shadow-lg shadow-primary/50 w-full h-48 md:h-64 object-cover"
              />
              <img
                src="https://images.unsplash.com/photo-1572021335469-31706a17aaef"
                alt="Team Collaboration"
                className="rounded-lg shadow-lg shadow-primary/50 w-full h-48 md:h-64 object-cover mt-4 md:mt-8"
              />
            </div>
            {/* Content second on mobile */}
            <div className="order-2 md:order-1">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 md:mb-6">Our Story</h2>
              <p className="text-base md:text-lg text-muted-foreground mb-3 md:mb-4 leading-relaxed">
                Founded over 15 years ago, ALM Group of Companies began with a simple mission: to provide world-class 
                business solutions that drive real results. What started as a small trading operation has evolved into 
                a comprehensive business conglomerate spanning four specialized divisions.
              </p>
              <p className="text-base md:text-lg text-muted-foreground mb-3 md:mb-4 leading-relaxed">
                Today, we serve over 250 clients across 50+ countries, delivering excellence in international trade, 
                strategic consulting, IT infrastructure, and custom software development. Our growth reflects our 
                unwavering commitment to client success and continuous innovation.
              </p>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                At ALM Group, we believe in building lasting partnerships. Every project, every consultation, and every 
                solution is crafted with precision, backed by expertise, and driven by a genuine desire to see our 
                clients thrive in an ever-evolving business landscape.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 hover:border-primary/50 hover:shadow-xl shadow-primary/20 transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-primary/20 rounded-xl flex items-center justify-center mb-6">
                  <Target className="text-primary" size={32} />
                </div>
                <h3 className="text-3xl font-bold text-foreground mb-4">Our Mission</h3>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  To empower businesses worldwide with innovative solutions that drive growth, optimize operations, 
                  and create lasting value. We are committed to delivering excellence through expertise, integrity, 
                  and unwavering dedication to our clients' success.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary/50 hover:shadow-xl shadow-primary/20 transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-primary/20 rounded-xl flex items-center justify-center mb-6">
                  <Eye className="text-primary" size={32} />
                </div>
                <h3 className="text-3xl font-bold text-foreground mb-4">Our Vision</h3>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  To be the world's most trusted business partner, recognized for transforming challenges into 
                  opportunities and ideas into reality. We envision a future where businesses thrive through 
                  innovation, collaboration, and sustainable growth across all sectors.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-12 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3 md:mb-4">Our Core Values</h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto px-4">
              The principles that guide everything we do
            </p>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: "Excellence",
                description: "We pursue excellence in every project, delivering quality that exceeds expectations"
              },
              {
                icon: UsersIcon,
                title: "Client-Focused",
                description: "Your success is our priority. We build solutions around your unique needs"
              },
              {
                icon: TrendingUp,
                title: "Innovation",
                description: "We embrace change and leverage cutting-edge solutions to stay ahead"
              },
              {
                icon: Globe,
                title: "Global Perspective",
                description: "We bring international expertise with local understanding to every engagement"
              },
              {
                icon: Target,
                title: "Integrity",
                description: "We conduct business with honesty, transparency, and ethical practices"
              },
              {
                icon: UsersIcon,
                title: "Collaboration",
                description: "We work as partners, combining our expertise with your vision"
              }
            ].map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="group text-center hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 border-2 hover:border-primary/50 hover:scale-105 cursor-pointer">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-primary/20 group-hover:bg-primary rounded-xl flex items-center justify-center mx-auto mb-6 transition-all duration-300 group-hover:rotate-12 group-hover:scale-110">
                      <Icon className="text-primary group-hover:text-white transition-colors duration-300" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-foreground group-hover:text-primary mb-3 transition-colors duration-300">{value.title}</h3>
                    <p className="text-muted-foreground group-hover:text-foreground transition-colors duration-300">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Mobile/Tablet Slider */}
          <div className="md:hidden">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              plugins={[
                Autoplay({
                  delay: 3500,
                }),
              ]}
              className="w-full"
            >
              <CarouselContent className="-ml-2">
                {[
                  {
                    icon: Award,
                    title: "Excellence",
                    description: "We pursue excellence in every project, delivering quality that exceeds expectations"
                  },
                  {
                    icon: UsersIcon,
                    title: "Client-Focused",
                    description: "Your success is our priority. We build solutions around your unique needs"
                  },
                  {
                    icon: TrendingUp,
                    title: "Innovation",
                    description: "We embrace change and leverage cutting-edge solutions to stay ahead"
                  },
                  {
                    icon: Globe,
                    title: "Global Perspective",
                    description: "We bring international expertise with local understanding to every engagement"
                  },
                  {
                    icon: Target,
                    title: "Integrity",
                    description: "We conduct business with honesty, transparency, and ethical practices"
                  },
                  {
                    icon: UsersIcon,
                    title: "Collaboration",
                    description: "We work as partners, combining our expertise with your vision"
                  }
                ].map((value, index) => {
                  const Icon = value.icon;
                  return (
                    <CarouselItem key={index} className="pl-2 basis-full sm:basis-3/4">
                      <Card className="group text-center hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 border-2 hover:border-primary/50 h-full">
                        <CardContent className="p-6 md:p-8">
                          <div className="w-16 h-16 bg-primary/20 group-hover:bg-primary rounded-xl flex items-center justify-center mx-auto mb-6 transition-all duration-300 group-hover:rotate-12 group-hover:scale-110">
                            <Icon className="text-primary group-hover:text-white transition-colors duration-300" size={32} />
                          </div>
                          <h3 className="text-xl font-bold text-foreground group-hover:text-primary mb-3 transition-colors duration-300">{value.title}</h3>
                          <p className="text-muted-foreground group-hover:text-foreground transition-colors duration-300">{value.description}</p>
                        </CardContent>
                      </Card>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselPrevious className="hidden sm:flex" />
              <CarouselNext className="hidden sm:flex" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* Our Divisions */}
      <section className="py-12 md:py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3 md:mb-4">Our Divisions</h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto px-4">
              Four specialized divisions delivering comprehensive solutions
            </p>
          </div>

          <div className="space-y-6 md:space-y-8">
            {divisions.map((division, index) => (
              <Card key={division.id} className="hover:shadow-xl shadow-primary/20 transition-shadow duration-300">
                <CardContent className="p-4 md:p-8">
                  <div className="grid md:grid-cols-3 gap-4 md:gap-8">
                    {/* Image first on mobile */}
                    <div className="md:col-span-1 order-1">
                      <img
                        src={division.image}
                        alt={division.name}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    </div>
                    {/* Content second on mobile */}
                    <div className="md:col-span-2 order-2">
                      <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">{division.name}</h3>
                      <p className="text-primary font-semibold mb-3 md:mb-4 text-sm md:text-base">{division.tagline}</p>
                      <p className="text-muted-foreground mb-4 text-sm md:text-base">{division.description}</p>
                      <Link to={`/services/${division.id}`}>
                        <Button className="bg-primary hover:bg-primary/90 text-white w-full sm:w-auto">
                          Explore {division.shortName}
                          <ArrowRight className="ml-2" size={16} />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-gradient-to-br from-primary/20 via-purple-600/10 to-background text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Impact</h2>
            <p className="text-xl text-muted-foreground">Numbers that reflect our commitment to excellence</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-foreground mb-6">
            Let's Build Something Great Together
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Partner with ALM Group and experience the difference that expertise and dedication make
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/50 hover:shadow-xl shadow-primary/20 transition-all duration-300">
                Get Started Today
              </Button>
            </Link>
            <Link to="/services/trading">
              <Button size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/10 shadow-md hover:shadow-lg shadow-primary/50 transition-all duration-300">
                Explore Our Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
